
import { useState, useEffect, useCallback, useRef } from 'react';

export interface NetworkStatusResult {
  isOnline: boolean;
  checkConnection: () => boolean;
}

export const useNetworkStatus = (): NetworkStatusResult => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const lastCheckTimeRef = useRef<number>(0);
  const checkIntervalRef = useRef<number | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Check active connection with more aggressive throttling
  const checkConnection = useCallback((): boolean => {
    const now = Date.now();
    // Increased throttling to 2 seconds
    if (now - lastCheckTimeRef.current < 2000) {
      return isOnline;
    }
    
    lastCheckTimeRef.current = now;
    const online = navigator.onLine;
    if (online !== isOnline) {
      setIsOnline(online);
    }
    return online;
  }, [isOnline]);
  
  // Set up event listeners for online/offline events with better debouncing
  useEffect(() => {
    const handleOnlineStatusChange = (online: boolean) => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // More aggressive debounce to prevent rapid toggling
      debounceTimerRef.current = setTimeout(() => {
        if (online !== isOnline) {
          console.log(`Network status changed: ${online ? 'online' : 'offline'}`);
          setIsOnline(online);
        }
        debounceTimerRef.current = null;
      }, 1000); // Increased debounce time for better stability
    };
    
    const handleOnline = () => handleOnlineStatusChange(true);
    const handleOffline = () => handleOnlineStatusChange(false);
    
    // Set initial status
    setIsOnline(navigator.onLine);
    
    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Significantly reduced check interval from 60s to 120s
    checkIntervalRef.current = window.setInterval(() => {
      const currentStatus = navigator.onLine;
      if (currentStatus !== isOnline) {
        console.log(`Network status changed: ${currentStatus ? 'online' : 'offline'}`);
        setIsOnline(currentStatus);
      }
    }, 120000); 
    
    // Clean up
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [isOnline]);
  
  return { isOnline, checkConnection };
};

export default useNetworkStatus;
